import React from 'react';
import { PiAddressBookLight, PiWhatsappLogoLight } from 'react-icons/pi';
import type {
  WebsiteAnalyticsResponse,
  WebsiteUpsertRequest,
  WebsiteWhatsAppCtaSlot,
} from 'src/api/generated';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { SectionHeading } from 'src/modules/shared/components/molecules/section-heading/section-heading';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import {
  isWebsiteContactEmailValid,
  isWebsiteContactHttpUrlValid,
} from '../../utils/website-contact-validation.util';
import { WEBSITE_WHATSAPP_CTA_CONFIGS } from '../../utils/website-whatsapp-cta.util';
import { WebsiteAnalyticsPanel } from './website-analytics-panel';

/** Website content edited in the contact step. */
type WebsiteContactContent = Pick<
  WebsiteUpsertRequest['draftConfig']['content'],
  | 'address'
  | 'email'
  | 'phone'
  | 'businessHours'
  | 'instagramUrl'
  | 'facebookUrl'
  | 'tiktokUrl'
>;

/** One public contact field of the contact step. */
interface WebsiteContactField {
  /** Content key the field edits. */
  key: keyof WebsiteContactContent;
  /** Translation key of the label. */
  labelKey: TranslationKey;
  /** Native input type. */
  type?: 'email' | 'tel' | 'url';
  /** Browser autocomplete hint. */
  autoComplete?: string;
  /** Translation key of the error shown while the value is invalid. */
  errorKey?: TranslationKey;
  /** Checks the value; fields without a validator are always valid. */
  isValid?: (value?: string) => boolean;
}

const CONTACT_FIELDS: readonly WebsiteContactField[] = [
  {
    key: 'address',
    labelKey: 'websites.settings.contact.address.label',
    autoComplete: 'street-address',
  },
  {
    key: 'email',
    labelKey: 'websites.settings.contact.email.label',
    type: 'email',
    autoComplete: 'email',
    errorKey: 'websites.settings.contact.validation.email',
    isValid: isWebsiteContactEmailValid,
  },
  {
    key: 'phone',
    labelKey: 'websites.settings.contact.phone.label',
    type: 'tel',
    autoComplete: 'tel',
  },
  {
    key: 'businessHours',
    labelKey: 'websites.settings.contact.businessHours.label',
  },
  {
    key: 'instagramUrl',
    labelKey: 'websites.settings.contact.instagramUrl.label',
    type: 'url',
    autoComplete: 'url',
    errorKey: 'websites.settings.contact.validation.socialUrl',
    isValid: isWebsiteContactHttpUrlValid,
  },
  {
    key: 'facebookUrl',
    labelKey: 'websites.settings.contact.facebookUrl.label',
    type: 'url',
    autoComplete: 'url',
    errorKey: 'websites.settings.contact.validation.socialUrl',
    isValid: isWebsiteContactHttpUrlValid,
  },
  {
    key: 'tiktokUrl',
    labelKey: 'websites.settings.contact.tiktokUrl.label',
    type: 'url',
    autoComplete: 'url',
    errorKey: 'websites.settings.contact.validation.socialUrl',
    isValid: isWebsiteContactHttpUrlValid,
  },
];

/** Props for the contact and WhatsApp step. */
interface WebsiteContactStepProps {
  /** Website draft currently in the editor. */
  form: WebsiteUpsertRequest;
  /** Whether the current user may not edit the website. */
  disabled: boolean;
  /** WhatsApp click analytics for the last 30 days. */
  analytics: WebsiteAnalyticsResponse | null;
  /** Whether the analytics request is running. */
  analyticsLoading: boolean;
  /** Whether the analytics request failed. */
  analyticsHasError: boolean;
  /** Called when the WhatsApp number changes. */
  onWhatsAppPhoneChange: (phoneNumber: string) => void;
  /** Called when a WhatsApp message template changes. */
  onMessageTemplateChange: (
    slot: WebsiteWhatsAppCtaSlot,
    messageTemplate: string
  ) => void;
  /** Called when a public contact field changes. */
  onContentChange: (content: Partial<WebsiteContactContent>) => void;
}

/**
 * Contact and WhatsApp step: the WhatsApp number and messages, the public
 * contact details, and the WhatsApp click analytics.
 *
 * @param props - Draft, analytics, and change callbacks.
 * @returns The contact step panel.
 */
export const WebsiteContactStep: React.FC<WebsiteContactStepProps> = ({
  form,
  disabled,
  analytics,
  analyticsLoading,
  analyticsHasError,
  onWhatsAppPhoneChange,
  onMessageTemplateChange,
  onContentChange,
}) => {
  const { t } = useI18n();
  const content = form.draftConfig.content;

  return (
    <section className="space-y-4">
      <SectionHeading
        title={t('websites.settings.contactStep.title')}
        description={t('websites.settings.contactStep.description')}
      />
      <InlineFormSection
        title={t('websites.settings.whatsApp.title')}
        description={t('websites.settings.whatsApp.description')}
        icon={<PiWhatsappLogoLight className="h-5 w-5" aria-hidden />}
      >
        <LabelTextInput
          label={t('websites.settings.whatsApp.phoneNumber.label')}
          labelPosition="top"
          inputProps={{
            'aria-label': t('websites.settings.whatsApp.phoneNumber.label'),
            type: 'tel',
            value: form.draftConfig.whatsApp.phoneNumber,
            disabled,
            autoComplete: 'tel',
            onChange: (event) => onWhatsAppPhoneChange(event.target.value),
          }}
        />
        <div className="space-y-3">
          {WEBSITE_WHATSAPP_CTA_CONFIGS.map((item) => (
            <LabelTextArea
              key={item.slot}
              label={t(item.labelKey)}
              labelPosition="top"
              inputProps={{
                rows: 3,
                value:
                  form.draftConfig.whatsApp.templates.find(
                    (template) => template.slot === item.slot
                  )?.messageTemplate ?? t(item.defaultMessageKey),
                disabled,
                onChange: (event) =>
                  onMessageTemplateChange(item.slot, event.target.value),
              }}
            />
          ))}
        </div>
        <Text size="tiny" color="secondary">
          {t('websites.settings.whatsApp.availableTokens')}
        </Text>
      </InlineFormSection>

      <InlineFormSection
        title={t('websites.settings.contact.title')}
        description={t('websites.settings.contact.description')}
        icon={<PiAddressBookLight className="h-5 w-5" aria-hidden />}
      >
        <div className="grid gap-4 md:grid-cols-2">
          {CONTACT_FIELDS.map((field) => {
            const value = content[field.key] ?? '';
            const isValid = field.isValid?.(value) ?? true;

            return (
              <LabelTextInput
                key={field.key}
                label={t(field.labelKey)}
                labelPosition="top"
                inputProps={{
                  'aria-label': t(field.labelKey),
                  type: field.type,
                  value,
                  error:
                    isValid || !field.errorKey ? undefined : t(field.errorKey),
                  'aria-invalid': field.isValid ? !isValid : undefined,
                  disabled,
                  autoComplete: field.autoComplete,
                  onChange: (event) =>
                    onContentChange({ [field.key]: event.target.value }),
                }}
              />
            );
          })}
        </div>
      </InlineFormSection>

      <WebsiteAnalyticsPanel
        analytics={analytics}
        loading={analyticsLoading}
        hasError={analyticsHasError}
      />
    </section>
  );
};
